import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./InsideOutside.css";

const GARMENTS = ["👕", "👗", "🩳"];

const GAME_CONFIG = {
  easy: { label: "Fácil", totalRounds: 10 },
  medium: { label: "Media", totalRounds: 15 },
  hard: { label: "Difícil", totalRounds: 20 },
};

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function createRound() {
  const correctAnswer = Math.random() > 0.5 ? "inside" : "outside";

  const insidePositions = [
    { x: 48, y: 42 },
    { x: 54, y: 50 },
    { x: 44, y: 55 },
    { x: 50, y: 62 },
  ];

  const outsidePositions = [
    { x: 18, y: 38 },
    { x: 80, y: 42 },
    { x: 28, y: 78 },
    { x: 74, y: 76 },
    { x: 50, y: 20 },
  ];

  return {
    garment: randomItem(GARMENTS),
    correctAnswer,
    stain:
      correctAnswer === "inside"
        ? randomItem(insidePositions)
        : randomItem(outsidePositions),
  };
}

export default function InsideOutside() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correct, setCorrect] = useState(0);
  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((acc, item) => acc + item.reactionTime, 0) /
            responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setRoundIndex(0);
    setCorrect(0);
    setErrors(0);
    setResponses([]);
    setStarted(true);
    setFinished(false);
    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
    setRound(createRound());
  };

  const answer = (value) => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;
    const isCorrect = value === round.correctAnswer;

    setResponses((prev) => [
      ...prev,
      {
        round: roundIndex + 1,
        answer: value,
        correctAnswer: round.correctAnswer,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) setCorrect((prev) => prev + 1);
    else setErrors((prev) => prev + 1);

    const nextRound = roundIndex + 1;

    if (nextRound >= config.totalRounds) {
      setFinished(true);
      setStarted(false);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRound);
    setRound(createRound());
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setStarted(false);
    setFinished(false);
    setRound(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Mancha dentro/fuera"
        description="Observa la prenda y decide si la mancha está dentro o fuera."
        areas={["Atención focalizada", "Atención visual", "Velocidad de respuesta"]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="inside-outside-page">
      <div className="inside-outside-card">
        <header className="inside-outside-header">
          <div>
            <h1>Mancha dentro/fuera</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="inside-outside-stage">
              <div className="garment">{round.garment}</div>

              <div
                className="paint-stain"
                style={{
                  left: `${round.stain.x}%`,
                  top: `${round.stain.y}%`,
                }}
              />
            </section>

            <section className="inside-outside-buttons">
              <button type="button" onClick={() => answer("inside")}>
                Dentro
              </button>

              <button type="button" onClick={() => answer("outside")}>
                Fuera
              </button>
            </section>

            <section className="inside-outside-score">
              <span>Aciertos: {correct}</span>
              <span>Errores: {errors}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Errores", value: errors },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}